import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaSearch, FaTimes, FaBirthdayCake, FaMicrophone, FaCamera } from 'react-icons/fa';
import { TbTruckDelivery } from 'react-icons/tb';
import logo from '@assets/pcakelogo.png';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFirebase } from '@services/firebase';
import { useEnquiryBadge } from '@hooks/useEnquiryBadge';
import '../index.css';

const searchPlaceholderStyle = `
  .topbar-search::placeholder { color: #444; font-size: 11px; }
  html[data-theme="dark"] .topbar-search-wrapper { background: #cdd1d8 !important; }
  html[data-theme="dark"] .topbar-search::placeholder { color: #333 !important; font-size: 11px; }
  /* Phones give every button a 48px minimum (index.css); in the search bar that pushed the
     camera towards the middle. Icon buttons here keep their own small size. */
  .topbar-search-wrapper button { min-width: 0 !important; min-height: 0 !important; width: auto; padding: 4px 5px !important; }
`;

const { auth } = getFirebase();

const TopBar = ({ onSearchChange, userRole }) => {
  const [user] = useAuthState(auth);
  const [searchQuery, setSearchQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const location = useLocation();
  const hasEnquiryUpdate = useEnquiryBadge(user, userRole);
  // Image search: the chosen photo goes to Home through a window event (Home ranks the cakes).
  const photoInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const handlePhotoChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setPhotoPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(file); });
    window.dispatchEvent(new CustomEvent('pcake:image-search', { detail: { file } }));
  };

  const clearPhoto = () => {
    setPhotoPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
    window.dispatchEvent(new CustomEvent('pcake:image-search', { detail: { file: null } }));
  };

  // Home clears the photo search too (its own "x" on the results line).
  useEffect(() => {
    const onCleared = () => setPhotoPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
    window.addEventListener('pcake:image-search-cleared', onCleared);
    return () => window.removeEventListener('pcake:image-search-cleared', onCleared);
  }, []);

  // A search belongs to the page it was typed on (Home, Custom, a shared link): changing page
  // starts with an empty bar and no photo.
  const searchPage = useRef(location.pathname);
  useEffect(() => {
    if (searchPage.current === location.pathname) return;
    searchPage.current = location.pathname;
    setSearchQuery('');
    onSearchChange('');
    setPhotoPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared link: start with the search the link was made with.
  useEffect(() => {
    if (location.pathname !== '/shared') return;
    try {
      const t = new URLSearchParams(location.search).get('t');
      const sq = t ? JSON.parse(atob(t)).sq : '';
      if (sq) { setSearchQuery(sq); onSearchChange(sq); }
    } catch { /* bad token — SharedView shows the message */ }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearchChange(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    onSearchChange('');
  };

  const supportsSpeech = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const handleVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    setIsListening(true);
    recognition.start();
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      onSearchChange(transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  };

  const isHomePage = location.pathname === '/';
  const isSharedPage = location.pathname === '/shared';
  // Super's Custom page searches its own cakes (text and photo), like Home.
  const isSuperCustom = location.pathname === '/custom' && userRole === 'Super';

  // Determine the icon and navigation based on user role
  const isMover = userRole === 'Mover';
  const isAdmin = userRole === 'Admin';
  const isBuyer = userRole === 'Buyer';
  const isSuper = userRole === 'Super';
  const isUser = userRole === 'User';
  const showCartIcon = isMover || isAdmin || isBuyer || isSuper;
  const IconComponent = isMover ? TbTruckDelivery : FaBirthdayCake;
  const navigationPath = isMover ? '/delorder' : isBuyer ? '/enquiry' :'/order';

  return (
    <div className="top-bar" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <style>{searchPlaceholderStyle}</style>
      {/* Conditionally render logo with Link for non-Movers, plain div for Movers */}
      {isMover || isSharedPage ? (
        <div>
          <img src={logo} alt="PCake Logo" className="logo" />
        </div>
      ) : (
        <Link to="/">
          <img src={logo} alt="PCake Logo" className="logo" />
        </Link>
      )}
      {(isSharedPage || (user && (isHomePage || isSuperCustom))) && (
        <div
          className="topbar-search-wrapper"
          style={{
            flex: 1,
            margin: '0 12px',
            background: '#f0f2f5',
            borderRadius: '8px',
            padding: '0 6px 0 10px',
            display: 'flex',
            alignItems: 'center',
            height: '35px',
            position: 'relative',
          }}
        >
          <input
            type="text"
            className="topbar-search"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by Theme, Flavour, Event, Shape or photo ..."
            style={{
              flex: 1,
              padding: '0 4px 0 0',
              border: 'none',
              background: 'transparent',
              fontSize: '14px',
              outline: 'none',
              color: '#333',
              minWidth: 0,
            }}
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#555',
                fontSize: '14px',
                padding: '4px',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              <FaTimes />
            </button>
          )}
          {photoPreview && (
            <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, marginRight: '2px' }}>
              <img src={photoPreview} alt="Search photo" style={{ width: '26px', height: '26px', objectFit: 'cover', borderRadius: '5px' }} />
              <button
                onClick={clearPhoto}
                title="Clear photo search"
                aria-label="Clear photo search"
                style={{
                  position: 'absolute', top: '-6px', right: '-6px', width: '15px', height: '15px', borderRadius: '50%',
                  border: 'none', background: '#555', color: '#fff', fontSize: '8px', padding: 0, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <FaTimes />
              </button>
            </span>
          )}
          <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoChosen} style={{ display: 'none' }} />
          {/* Camera + microphone sit together at the right end of the bar. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', marginLeft: 'auto', flexShrink: 0 }}>
          <button
            onClick={() => photoInputRef.current?.click()}
            title="Search by photo"
            aria-label="Search by photo"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: photoPreview ? '#e53935' : '#555',
              fontSize: '14px',
              padding: '4px',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            <FaCamera />
          </button>
          {supportsSpeech && (
            <button
              onClick={handleVoiceSearch}
              title={isListening ? 'Listening...' : 'Voice search'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: isListening ? '#e53935' : '#555',
                fontSize: '14px',
                padding: '4px',
                lineHeight: 1,
                flexShrink: 0,
                transition: 'color 0.2s',
              }}
            >
              <FaMicrophone />
            </button>
          )}
          </span>
        </div>
      )}
      {showCartIcon && (
        <NavLink to={navigationPath} className={({ isActive }) => isActive ? 'cart-icon-link cart-icon-link--active' : 'cart-icon-link'}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <IconComponent className="cart-icon" />
            {isBuyer && hasEnquiryUpdate && (
              <span
                style={{
                  position: 'absolute',
                  bottom: '-4px',
                  right: '-4px',
                  width: '9px',
                  height: '9px',
                  backgroundColor: '#4caf50',
                  borderRadius: '50%',
                  border: '1.5px solid #fff',
                  display: 'block',
                }}
              />
            )}
          </div>
        </NavLink>
      )}
    </div>
  );
};

export default TopBar;